export interface StudentDataItem {
  key: string;
  value: string;
}

export const loanStatusDescriptionRemapping: Record<string, string> = {
  // 'NON-DEFAULTED, PAID IN FULL THROUGH CONSOLIDATION LOAN': 'PAID IN FULL THROUGH CONSOLIDATION',
  // 'LOAN ORIGINATED': 'NEW LOAN',
  // Add other mappings here as needed
};

// Add Loan Type Group Mapping
export const LOAN_TYPE_GROUP_MAP: Record<string, string> = {
  // XX: 'Private',
  D0: 'Direct',
  D1: 'Direct',
  D2: 'Direct',
  D3: 'Direct',
  D4: 'Direct Plus Parent',
  D5: 'Direct',
  D6: 'Direct',
  D7: 'Direct Plus Consolidation',
  D8: 'Direct',
  D9: 'Direct',
  CL: 'FFEL',
  GB: 'FFEL',
  PL: 'FFEL Plus Parent',
  SF: 'FFEL',
  SL: 'FFEL',
  SU: 'FFEL',
  SN: 'FFEL',
  PU: 'Perkins',
  DU: 'Perkins',
  NU: 'Perkins',
  EU: 'Perkins',
  RF: 'Other',
  FI: 'Other',
  IC: 'Other',
};

// Derive unique group names for Select component
export const LOAN_TYPE_GROUPS = [...new Set(Object.values(LOAN_TYPE_GROUP_MAP))];

export interface LoanTypeTotal {
  'Type Code'?: string;
  'Type Description'?: string;
  'Total Outstanding Principal'?: string;
  'Total Outstanding Interest'?: string;
  [key: string]: string | undefined;
}

// GS (Google Script) inspired type for identifying the borrower in calculations
export type LoanBorrowerGS = 'Me' | 'My partner';

// Interface for data directly parsed from the NSLDS file
export interface NsldsLoan {
  'Loan Award ID': string;
  'Loan Type Code'?: string;
  'Loan Type Description'?: string;
  'Loan Date'?: string;
  'Loan Attending School Name'?: string;
  'Loan Amount'?: string; // Raw value from file
  'Loan Interest Rate'?: string; // Raw value from file (e.g., "5.0%")
  'Loan Contact Name'?: string;
  'Current Loan Status'?: string;
  'Current Loan Status Description'?: string; // Original description
  'Outstanding Principal'?: string; // Raw value from file
  'Outstanding Interest'?: string; // Raw value from file
  // Note: The NsldsLoan interface had fields like Borrower, qualifiedPaymentMonths etc.
  // These are typically part of the processed Loan interface, not raw NsldsLoan.
  // Keeping NsldsLoan focused on raw parseable fields.
}

// Interface for the final Loan object used throughout the application
export interface Loan {
  'Loan Award ID': string;
  'Loan Name'?: string; // Added back for manual loan entry identification
  'Loan Type Code'?: string;
  'Loan Type Description'?: string;
  'Loan Type Group': string; // Derived field
  'Loan Date'?: string;
  'Loan Attending School Name'?: string;
  'Loan Amount'?: number; // Parsed numeric value
  'Loan Interest Rate': number; // Changed to required
  'Loan Contact Name'?: string;
  'Current Loan Status'?: string;
  'Current Loan Status Description': string; // Remapped description
  'Outstanding Principal': number; // Parsed numeric value
  'Outstanding Interest': number; // Parsed numeric value - this is the current non-capitalized interest.
  'Total Outstanding': number; // Calculated field (Principal + NonCapInterest)
  'Loan PSLF Cumulative Matched Months': number; // Parsed numeric value
  'Consolidation Loan With Any Parent Plus Indicator'?: 'Yes' | 'No' | null; // Added field
  // Borrower type for general app use. Needs mapping to LoanBorrowerGS for calculations.
  Borrower: 'user' | 'partner'; // Changed from user | partner | manual
  qualifiedPaymentMonths: number; // General IDR qualified months at simulation start.
  'Current Payment Plan'?: string | null;
  firstDisbursementDate?: string; // Input field
  isInRepayment: boolean;
  manualIbrEligibilityCheck?: 'parentPlus' | 'notParentPlus' | null; // For manual loans

  // --- Fields now considered required for new calculation logic ---
  originalPrincipal: number; // Principal at origination.
  degreeType: 'undergraduate' | 'graduate'; // Required for SAVE forgiveness, etc.
  isPartnerLoan?: boolean; // Added to distinguish partner's manual loans
}

export interface LoanEvent {
  'Loan Award ID': string | null;
  'Loan Status'?: string;
  'Loan Status Description'?: string; // Store potentially remapped description here too?
  'Loan Status Effective Date'?: string;
  // Remove index signature if LoanEvent fields are fixed
  [key: string]: string | undefined | null; // Keep for now if event structure varies
}

export interface ParsedData {
  studentData: StudentDataItem[];
  loansData: Loan[]; // Parser outputs the final Loan structure
  loanEventsData: LoanEvent[];
  allLoanKeys: Set<keyof NsldsLoan>; // Keys relevant during parsing phase
  fileRequestDate: string | null;
}
